// Japanese lofi / "wa" lofi, a Kyoto teahouse after dark (docs/AUDIO_DESIGN.md §12): a koto plucks open chords (sus,
// fourths and fifths, ♭II and iv in minor, few thirds) in arpeggios, flams and tremolo with the oshide press bending its
// strings, a shakuhachi (or a second koto) plays slow phrases in the in / in-sen scale (minor) or yo / major pentatonic
// (major), over a soft round bass and a soft boom-bap with mokugyo wood block ticks, a taiko on some downbeats and a rin.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote, snapToKey, playedChords } from '../harmony.js';
import { createKoto, createKotoLead } from './japanese-koto.js';
import { SCALES, toScale, scaleOf, createShakuhachi, createWaBass, createWaKit } from './japanese-band.js';

const mod12 = n => ((n % 12) + 12) % 12;
const E_IX = { chill: 0, balanced: 1, upbeat: 2 };
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};
const perms = a => (a.length <= 1 ? [a] : a.flatMap((x, i) => perms([...a.slice(0, i), ...a.slice(i + 1)]).map(p => [x, ...p])));

/* ---------------- koto voicings: a bass string on the root + 3 upper strings, open ---------------- */
// The upper strings' tones (semitones over the root) per quality, in every order (a list with a repeat, the open fifth's
// 5-R-5 or R-5-R, only as written), each string the nearest above the last at least a whole step up (a minor 3rd under
// MIDI 55), from MIDI 52–64, top 60–76, upper span ≤ 15; the bass string is the root at MIDI 43–55, a 4th or more under
// the upper strings (a minor 3rd from MIDI 48). The engine's final tonic is jp69 in major, jpsus4 in minor.
const TONES = {
  jp5: [[7, 0, 7], [0, 7, 0]], jpsus2: [[0, 2, 7]], jpsus4: [[0, 5, 7]], jpq: [[0, 5, 10]], jp69: [[2, 7, 9]], jpmaj9: [[7, 11, 2]],
  add9: [[2, 4, 7]], madd9: [[2, 3, 7]], maj7: [[4, 7, 11]], m7: [[3, 7, 10]],
};
const qOf = (c, plan) => {
  if (TONES[c.q]) return c.q;
  if (c.final) return plan.key.mode === 'minor' ? 'jpsus4' : 'jp69';
  const iv = QUALITIES[c.q] || [];
  return iv.includes(4) ? 'add9' : iv.includes(3) ? 'madd9' : 'jp5';
};
/** openness: −.8 for each 4th or 5th between neighbouring strings, +1.2 for each 3rd, +.25 for a whole step (few thirds
 *  stacked jazz-style: the koto's chords are fourths, fifths and seconds) */
const openness = v => v.reduce((s, m, i) => { if (!i) return s; const d = m - v[i - 1]; return s + (d === 5 || d === 7 ? -0.8 : d === 3 || d === 4 ? 1.2 : d === 2 ? 0.25 : 0); }, 0);
const CANDS = new Map();                                     // `${root}:${q}` → [{ v, open, mean }]
function candidates(root, q) {
  const key = `${root}:${q}`;
  if (CANDS.has(key)) return CANDS.get(key);
  const out = [], seen = new Set(), basses = [];
  for (let m = 43 + mod12(root - 43); m <= 55; m += 12) basses.push(m);
  for (const set of TONES[q]) for (const order of (new Set(set).size < set.length ? [set] : perms(set))) {
    for (let u0 = 52 + mod12(root + order[0] - 52); u0 <= 64; u0 += 12) {
      const u = [u0];
      for (let k = 1; k < order.length; k++) { let m = u[k - 1] + (u[k - 1] < 55 ? 3 : 2); m += mod12(root + order[k] - m); u.push(m); }
      if (u[2] < 60 || u[2] > 76 || u[2] - u[0] > 15) continue;
      for (const b of basses) {
        const v = [b, ...u], id = v.join();
        if (u0 - b < (b < 48 ? 5 : 3) || seen.has(id)) continue;
        seen.add(id);
        out.push({ v, open: openness(v), mean: (u[0] + u[1] + u[2]) / 3 });
      }
    }
  }
  CANDS.set(key, out);
  return out;
}
/** Cost: Σ|Δ| of the upper strings + .4·|Δbass| + 2·max(0, |Δtop| − 3) + .5·|mean(upper) − home| + .3·|bass − 48| + the
 *  openness, home = 64 + plan.register (−5..+5, so tracks don't all open on the same notes). The same chord keeps its
 *  voicing (re-plucks and 2-bar chords). */
export function kotoVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), q = qOf(c, plan), home = 64 + (plan.register || 0);
  if (prev && prev.length === 4 && mod12(prev[0]) === root) {
    const want = new Set([0, ...TONES[q].flat()].map(i => mod12(root + i))), have = new Set(prev.map(mod12));
    if (want.size === have.size && [...want].every(pc => have.has(pc))) return prev;
  }
  let best = null, bc = Infinity;
  for (const x of candidates(root, q)) {
    const [b, u0, u1, u2] = x.v;
    let k = 0.5 * Math.abs(x.mean - home) + 0.3 * Math.abs(b - 48) + x.open;
    if (prev && prev.length === 4) k += Math.abs(u0 - prev[1]) + Math.abs(u1 - prev[2]) + Math.abs(u2 - prev[3]) + 0.4 * Math.abs(b - prev[0]) + 2 * Math.max(0, Math.abs(u2 - prev[3]) - 3);
    if (k < bc) { bc = k; best = x.v; }
  }
  return best || [43 + mod12(root - 43), 55 + mod12(root + 7 - 55), 60 + mod12(root - 60), 62 + mod12(root + 7 - 62)];
}

/* ---------------- bass: soft, round and sparse ---------------- */
// One pattern per track (another on 20 % of bars): hold (a long root per chord) · two (the root, then the 5th or the root
// again on the "and" of 3) · lilt (root, a soft 5th pickup on the "and" of 2, root on 3). A root on every chord; an
// approach into the next chord's root on step 14, a step of the track's scale above or below it (p .15 / .25 / .35); a
// fill's stop cuts the line; the break and the intro's last bar hold whole roots, the final one rings 28 steps.
const BASS_ALT = { hold: 1, two: 1, lilt: 0.6 };
function waBass({ plan, st, b, mode, chords, nextChords, stop }) {
  const pc = c => mod12(plan.key.tonic + c.root), out = [], end = stop ?? 16;
  const add = (s, midi, vel, dur) => { if (s < end) out.push({ s, midi, vel, dur: stop != null ? Math.min(dur, end - s) : dur }); };
  if (mode === 'whole' || mode === 'last' || (mode === 'outro' && b.j === 2)) {
    for (const c of chords) { const m = bassNote(pc(c), st.prevBass); add(c.start * 4, m, 0.72, mode === 'outro' ? 28 : c.beats * 4 * 0.95); st.prevBass = m; }
    return out;
  }
  const pat = st.rS() < 0.2 ? pickW(st.rS, BASS_ALT) : plan.japanese.bass, f = plan.bass.factor;
  chords.forEach(c => {
    const c0 = c.start * 4, c1 = c0 + c.beats * 4, root = bassNote(pc(c), st.prevBass), iv = QUALITIES[c.q] || [0, 7];
    const fifth = iv.includes(7) ? (root + 7 <= 47 ? root + 7 : root - 5) : root, v0 = c.onset === false ? 0.66 : 0.84;
    if (pat === 'hold' || c1 - c0 < 16) add(c0, root, v0, (c1 - c0) * f);
    else if (pat === 'two') { add(c0, root, v0, 8 * f); add(10, st.rS() < 0.5 ? fifth : root, 0.66, 5 * f); }
    else { add(c0, root, v0, 5 * f); add(6, fifth, 0.5, 1.6); add(8, root, 0.74, 7 * f); }
    st.prevBass = root;
  });
  const nx = nextChords && nextChords[0], last = chords[chords.length - 1], E = E_IX[plan.vibe.energy] ?? 1;
  if (nx && pc(nx) !== pc(last) && st.rS() < [0.15, 0.25, 0.35][E] && end > 14) {
    const target = bassNote(pc(nx), st.prevBass), inS = scaleOf(plan.key, plan.japanese.scale), dir = st.rS() < 0.5 ? -1 : 1;
    let a = null;
    for (const d of [dir, 2 * dir, -dir, -2 * dir]) if (a == null && inS(target + d) && target + d >= 33 && target + d <= 50) a = target + d;
    if (a != null) {
      for (const n of out) n.dur = Math.min(n.dur, 14 - n.s - 0.3);
      add(14, a, 0.6, 1.8);
    }
  }
  return out.filter(n => n.dur > 0.2);
}

/* ---------------- melody: slow, breathing phrases (the shakuhachi's) in place of the jazzhop motif ---------------- */
// 2–5 notes over 2 bars with long notes, shaped as an arch (a valley on 45 % of tracks): the contour climbs to a peak
// note one scale step (p .65) or two at a time and comes back down the same way, so a phrase ends near where it began
// and the melody doesn't drift to the edge of its range; a note breathes before the next (p .55: re-blown) or slurs into
// it; the last note lasts 8–12 steps. The engine still picks the notes (the key's pentatonic, chord tones on beats 1 and
// 3); the lead maps them into the track's scale.
const WCELLS = [[0, 8], [0, 6, 8], [0, 10], [2, 8, 12], [0, 3, 8], [0, 8, 14], [4, 8, 12], [0, 12]];
const WTAILS = [[], [], [16], [18], [16, 22], [20]];
function wabiMotif(r, key) {
  const steps = WCELLS[Math.floor(r() * WCELLS.length)].concat(WTAILS[Math.floor(r() * WTAILS.length)]), n = steps.length;
  const last = 8 + Math.floor(r() * 5);
  const durs = steps.map((s, i) => { if (i === n - 1) return last; const gap = steps[i + 1] - s; return Math.min(10, r() < 0.55 ? Math.max(1, gap - 2) : gap); });
  const peak = 1 + Math.floor(r() * Math.max(1, n - 1)), sign = r() < 0.45 ? -1 : 1, contour = [0];
  for (let i = 1; i < n; i++) contour.push(contour[i - 1] + sign * (i <= peak ? 1 : -1) * (r() < 0.65 ? 1 : 2));
  const vel = steps.map((s, i) => (i === 0 ? 0.78 : +(0.64 + 0.08 * r()).toFixed(3)));
  return { steps, durs, contour, vel, mode: key.mode };
}

/* ---------------- comping: koto cells of [step, length, velocity, pick] ---------------- */
// Voicing indices: 0 = the bass string, 1–3 the upper strings (-1 = the top). Every cell sounds the bass on steps 0 and 8
// (or rolls the chord), so the 2nd chord of a split bar has its root. The koto reads the picks: one note a pluck, two a
// kaki flam (or awase together, wider than a 6th), three or more a roll, a note picked twice a tremolo.
const COMPS = {
  hold: [[0, 16, 1], [8, 8, 0.62, [0, -1]]],                                                          // a roll, then bass + top
  ma: [[0, 16, 1], [8, 8, 0.66, [0]], [11, 5, 0.55, [-1]]],                                           // space: a roll, the bass, one string
  trem: [[0, 16, 1], [8, 8, 0.58, [-1, -1]]],                                                         // a roll, then the top in tremolo
  arp: [[0, 16, 1, [0]], [2, 14, 0.68, [1]], [4, 12, 0.74, [2]], [6, 10, 0.8, [3]], [8, 8, 0.86, [0]], [10, 6, 0.68, [2]], [12, 4, 0.74, [3]], [14, 2, 0.62, [1]]],
  fall: [[0, 16, 1, [0, -1]], [3, 13, 0.66, [-2]], [6, 10, 0.7, [-3]], [8, 8, 0.86, [0]], [10, 6, 0.66, [-1]], [12, 4, 0.66, [-2]], [14, 2, 0.6, [-3]]],  // cascading down
  kaki: [[0, 8, 1, [0]], [4, 4, 0.74, [1, 2]], [8, 8, 0.9, [0]], [12, 4, 0.74, [2, 3]]],               // thumb, flams on 2 and 4
  pulse: [[0, 8, 1, [0, -1]], [2, 2, 0.56, [-2]], [4, 4, 0.7, [-1]], [6, 2, 0.56, [-2]], [8, 8, 0.9, [0, -1]], [10, 2, 0.56, [-2]], [12, 4, 0.7, [-1]], [14, 2, 0.56, [-2]]],  // warizume 8ths
  run: [[0, 4, 1, [0]], [1, 3, 0.58, [1]], [2, 2, 0.62, [2]], [3, 3, 0.66, [3]], [4, 4, 0.6, [2]], [6, 2, 0.58, [1]], [8, 4, 0.9, [0]], [9, 3, 0.58, [1]], [10, 2, 0.62, [2]], [11, 3, 0.66, [3]], [12, 4, 0.62, [2]], [14, 2, 0.58, [1]]],
};

/* ---------------- drums: a soft boom-bap with temple wood ---------------- */
// K kick · S soft snare · H hat · R wooden clack · W mokugyo · T taiko · X the rin bell
const GRIDS = {
  //         0123456789abcdef
  hana: { K: 'x.........x.....', S: '....x.......x...', H: 'x.s.x.s.x.s.x.s.', W: '.......s......s.' },                  // boom-bap, two wood ticks
  mizu: { K: 'x......x..x.....', S: '....x.......x...', H: '..s...s...s...s.', W: 'x.....s...s.....' },                  // off-beat hats, a mokugyo figure
  kaze: { K: 'x.....x.........', S: '........x.......', H: 's.s.s.s.s.s.s.s.', R: '............s...' },                  // halftime
  taiko: { T: 'x...............', K: '......s...x.....', S: '....x.......x...', H: 'x.s.x.s.x.s.x.s.', W: '..........s...s.' },   // the taiko on 1
  tera: { K: 'x.........x.....', R: '....x.......x...', W: 's...s...s...s...', H: '..s.......s.....' },                  // temple: mokugyo quarters
  break: { W: 's...s...s...s...', X: 'x...............' },                                                             // mokugyo and the rin
};
const GROOVES = {
  chill: { kaze: 1.6, tera: 1.2, hana: 0.6, taiko: 0.3 },
  slow: { hana: 1.2, kaze: 1, tera: 0.9, taiko: 0.5, mizu: 0.4 },
  fast: { hana: 1.2, mizu: 1, taiko: 0.8, tera: 0.5, kaze: 0.3 },
  upbeat: { hana: 1, mizu: 1.2, taiko: 1, tera: 0.3 },
};

export default {
  id: 'japanese', name: 'Japanese', desc: 'Koto, shakuhachi flute and Japanese scales over a soft wooden beat',
  //            chill                          balanced                       upbeat
  vary: 'colour',
  energy: {
    stretch:    [0.55,                         0.4,                           0.25],
    bpm:        [[62, 70],                     [68, 80],                      [76, 88]],
    swing:      [[56, 60],                     [54, 58],                      [53, 57]],
    snareLag:   [[0.01, 0.018],                [0.007, 0.014],                [0.004, 0.01]],
    groove:     [[GROOVES.chill, GROOVES.chill], [GROOVES.slow, GROOVES.fast], [GROOVES.upbeat, GROOVES.upbeat]],
    drumLevel:  [0.7,                          0.8,                           0.88],
    kitLp:      [[4000, 5600],                 [4800, 6800],                  [5600, 7800]],
    epLp:       [[2800, 3900],                 [3300, 4600],                  [3900, 5400]],     // the koto bus low-pass
    strum:      [[0.045, 0.07],                [0.035, 0.06],                 [0.028, 0.048]],   // a roll across the strings
    epVel:      [[0.5, 0.6],                   [0.54, 0.64],                  [0.56, 0.68]],
    comp: [
      { hold: [0.8, 1.4], ma: [0.6, 1.2], trem: [0.2, 0.5], arp: [0.2, 0.6], fall: [0.1, 0.4], kaki: [0, 0.2], pulse: [0, 0], run: [0, 0] },
      { hold: [0.1, 0.4], ma: [0.2, 0.5], trem: [0.2, 0.5], arp: [0.6, 1.1], fall: [0.4, 0.9], kaki: [0.3, 0.7], pulse: [0.1, 0.4], run: [0, 0.15] },
      { hold: [0, 0.1], ma: [0, 0.2], trem: [0.1, 0.4], arp: [0.5, 1], fall: [0.4, 0.8], kaki: [0.4, 0.9], pulse: [0.4, 0.9], run: [0.1, 0.4] },
    ],
    push:       [0.04,                         0.08,                          0.14],
    legato:     [0.8,                          0.7,                           0.55],
    lead:       [0.72,                         0.8,                           0.86],
    softLead:   [0.7,                          0.65,                          0.6],              // odds of the shakuhachi over a koto melody
    tone:       [[4200, 6200],                 [5000, 7400],                  [6000, 8600]],
    wowCents:   [[6, 11],                      [5, 9],                        [4, 8]],
    dust:       [[1, 1.6],                     [0.8, 1.4],                    [0.6, 1.2]],
    introHats:  [0.3,                          0.45,                          0.6],
    leadFrom:   [2,                            2,                             1],
    hatsFirst:  [0.4,                          0.3,                           0.2],
    breakDrums: [0.35,                         0.55,                          0.75],
    breakLead:  [0.45,                         0.5,                           0.6],
    oshide:     [[0.18, 0.32],                 [0.14, 0.28],                  [0.12, 0.24]],     // odds of a press on a long koto note
    ornament:   [[0.1, 0.2],                   [0.12, 0.24],                  [0.14, 0.26]],     // shakuhachi grace notes
    nayashi:    [[0.5, 0.75],                  [0.4, 0.65],                   [0.3, 0.55]],      // a long note starts flat and rises
    insen:      [0.3,                          0.35,                          0.4],              // minor: in-sen (♭7) over in (♭6)
    pentaMajor: [0.3,                          0.35,                          0.4],              // major: the major pentatonic over yo
    wabass:     [{ hold: 1, two: 0.5, lilt: 0.2 }, { hold: 0.7, two: 1, lilt: 0.5 }, { hold: 0.4, two: 1, lilt: 0.8 }],
    // development (arrange.js BY_ENERGY): no lift (the scale), no solo (the shakuhachi breathes); the koto alone or the flute first
    lift:       [0, 0, 0],
    solo:       [0, 0, 0],
    intro:      [{ keys: 0.45, beat: 0.1, groove: 0, lead: 0.4, cold: 0.05 }, { keys: 0.45, beat: 0.1, groove: 0, lead: 0.4, cold: 0.05 }, { keys: 0.45, beat: 0.1, groove: 0, lead: 0.4, cold: 0.05 }],
  },
  qualities: { jp5: [0, 7, 12], jpsus2: [0, 2, 7], jpsus4: [0, 5, 7], jpq: [0, 5, 10], jp69: [0, 2, 7, 9], jpmaj9: [0, 7, 11, 14] },
  progressions: {
    major: {
      y1: [[0, 'jpsus2', 4], [5, 'add9', 4], [0, 'jpsus2', 4], [7, 'jpsus4', 4]],                     // Isus2 · IVadd9 · Isus2 · Vsus4
      y2: [[5, 'add9', 4], [7, 'jpq', 4], [9, 'jpq', 4], [0, 'jpsus2', 4]],                          // IVadd9 · V(4ths) · vi(4ths) · Isus2
      y3: [[0, 'jp69', 4], [2, 'm7', 4], [5, 'add9', 4], [0, 'jpsus4', 2], [0, 'jpsus2', 2]],        // I6/9 · ii7 · IVadd9 · Isus4|Isus2
      y4: [[9, 'jpq', 4], [5, 'add9', 4], [0, 'jpsus2', 4], [0, 'jpsus2', 4]],                       // vi(4ths) · IVadd9 · Isus2
      y5: [[0, 'jp5', 4], [2, 'm7', 4], [0, 'jp5', 4], [5, 'add9', 4]],                              // I5 · ii7 · I5 · IVadd9
      y6: [[5, 'jpmaj9', 4], [0, 'jpsus2', 4], [2, 'm7', 4], [7, 'jpsus4', 4]],                      // IVmaj9 · Isus2 · ii7 · Vsus4
    },
    minor: {
      n1: [[0, 'jpsus4', 4], [1, 'maj7', 4], [0, 'jpsus4', 4], [0, 'jp5', 4]],                       // isus4 · ♭IImaj7 · isus4 · i5
      n2: [[5, 'madd9', 4], [1, 'maj7', 4], [0, 'jpsus4', 4], [0, 'jpsus4', 4]],                     // iv(add9) · ♭IImaj7 · isus4
      n3: [[8, 'jpsus2', 4], [1, 'maj7', 4], [0, 'jp5', 4], [7, 'jpq', 4]],                          // ♭VIsus2 · ♭IImaj7 · i5 · v(4ths)
      n4: [[0, 'jp5', 4], [10, 'jpq', 4], [8, 'jpsus2', 4], [1, 'maj7', 2], [0, 'jpsus4', 2]],       // i5 · ♭VII(4ths) · ♭VIsus2 · ♭II|isus4
      n5: [[0, 'jpsus4', 4], [5, 'madd9', 4], [1, 'maj7', 4], [1, 'maj7', 4]],                       // isus4 · iv(add9) · ♭IImaj7
      n6: [[5, 'madd9', 4], [7, 'jpq', 4], [8, 'jpsus2', 4], [0, 'jpsus4', 2], [0, 'jp5', 2]],       // iv(add9) · v(4ths) · ♭VIsus2 · isus4|i5
    },
  },
  // the bar that ends progB when it repeats progA: IVadd9|Vsus4 in major, ♭VIsus2|v(4ths) in minor
  turnarounds: { major: [[5, 'add9', 2], [7, 'jpsus4', 2]], minor: [[8, 'jpsus2', 2], [7, 'jpq', 2]] },
  grids: GRIDS,
  fills: {
    don: { roll: [[12, 0.62], [14, 0.8]], voice: 'tom' },                                            // taiko pickups: don . don
    kachi: { roll: [[12, 0.45], [13, 0.4], [14, 0.55], [15, 0.7]], voice: 'block' },                  // the mokugyo speeds up
    ma: { stop: 12 },                                                                                 // silence: the koto rings on
    rin: { roll: [[14, 0.8]], voice: 'shaker' },                                                      // the rin before the next phrase
  },
  bVariation: false,
  comps: COMPS,
  keysLevel: 1.6,

  plan(plan, { r, E }) {
    const rr = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d), minor = plan.key.mode === 'minor';
    plan.japanese = {
      scale: minor ? (r() < E.insen ? 'insen' : 'in') : (r() < E.pentaMajor ? 'major' : 'yo'),
      oshide: rr(...E.oshide), orn: rr(...E.ornament), nayashi: rr(...E.nayashi), vib: rr(14, 24, 1), rate: rr(4.4, 5.4, 2),
      echo: r() < 0.6 ? 3 : 4, bass: pickW(r, E.wabass),
    };
    plan.bass.lp = rr(420, 520, 0);
    const k = plan.drums.kit;
    plan.drums.kit = {
      kickF0: rr(110, 130, 1), kickF1: rr(46, 52, 1), kickDecay: rr(0.14, 0.2), snareBP: rr(1600, 2200, 0), snareDecay: rr(0.08, 0.12),
      hatHP: rr(6400, 8000, 0), hatClosed: rr(0.011, 0.016), rimF: rr(950, 1250, 0), blockF: +snapToKey(rr(520, 720, 0), plan.key).toFixed(1),
      blockDecay: rr(0.055, 0.08), taikoF0: rr(118, 135, 1), taikoF1: rr(66, 78, 1), taikoDecay: rr(0.34, 0.48),
      rinF: +snapToKey(rr(2300, 2800, 0), plan.key).toFixed(1), lp: k.lp,
    };
    if (plan.lead) Object.assign(plan.lead, { timbre: plan.lead.timbre === 'soft' ? 'shakuhachi' : 'koto', motif: wabiMotif(r, plan.key) });
  },

  voicing: kotoVoicing,
  bass: waBass,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    const ep = createKoto(ac, dry, wet, plan, { ...o, cap: ctx.cap, r: sub(), log: ctx.log, stepDur: ctx.stepDur });
    ep.warm(trackChords(plan).flat(), 3);
    let lead = null;
    if (plan.lead && plan.lead.timbre === 'koto') {
      lead = createKotoLead(ac, dry, wet, plan, { ...o, r: sub(), stepDur: ctx.stepDur });
      const notes = [];
      for (let m = 62; m <= 88; m++) if (SCALES[plan.japanese.scale].includes(mod12(m - plan.key.tonic))) notes.push(m);
      lead.warm(notes);
    } else if (plan.lead) lead = createShakuhachi(ac, dry, wet, plan, { ...o, stepDur: ctx.stepDur, r: sub() });
    return { ep, bass: createWaBass(ac, dry, plan.bass, o), kit: createWaKit(ac, dry, wet, plan.drums.kit, o), lead };
  },
};

/** the track's voicings, first bars first (progA, progB, the final tonic as in engine.js barChords), to render their
 *  strings ahead of time */
function trackChords(plan) {
  const out = [], fin = { root: 0, q: plan.key.mode === 'minor' ? 'm9' : 'maj9', final: true };
  let prev = null;
  for (const [c, P] of playedChords(plan, fin)) { prev = kotoVoicing(null, prev, c, P); out.push(prev); }
  return out;
}
